// /crm/prospects/:id/follow-up — Email Follow-Up management.
//
// Manages a prospect's enrollment in one of the four ActiveCampaign
// nurture sequences (Discovery No-Show / No Strategy / Strategy
// No-Show / No Engagement).
//
// What you can do here:
//   - Set ICP + Track tags (constrained dropdowns, written through to
//     AC as tags on the contact)
//   - Fill personalization slots (discovery pain point, finding 1-3 —
//     written through to AC as custom fields on the contact)
//   - Start one of the four sequences. Required-field validation is
//     enforced inline; the start button is disabled until the
//     sequence's required slots are populated.
//   - Stop the active sequence with a reason
//   - See current sequence state (active sequence, started, last sent)
//   - See per-email engagement (sent / opened / clicked / replied)
//
// All AC writes are server-side via /api/data/email-followup/*.
// Auto-saves personalization changes (debounced 800ms) so partial
// edits aren't lost on accidental navigation.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiFetch } from '../../utils/apiFetch';
import { usePipeline } from '../../context/PipelineContext';
import MeetingFormLayout from './MeetingFormLayout';

const ICP_OPTIONS = [
  { value: '', label: '— select —' },
  { value: 'business-owner', label: 'Business Owner' },
  { value: 'rental-owner', label: 'Rental Owner' },
  { value: 'retiree', label: 'Retiree' },
];
const TRACK_OPTIONS = [
  { value: '', label: '— select —' },
  { value: 'tax-plan', label: 'Tax Plan' },
  { value: 'bgw', label: 'Bullogic Guided Wealth' },
];
const SEQUENCES = [
  {
    id: 'discovery-noshow',
    label: 'Discovery No-Show',
    desc: 'Reschedule. Generic — no personalization required.',
    required: [],
  },
  {
    id: 'no-strategy',
    label: 'No Strategy Booked',
    desc: 'Discovery happened, prospect hasn\'t booked Strategy. Variants by ICP + Track.',
    required: ['icpTag', 'trackTag', 'discoveryPainPoint'],
  },
  {
    id: 'strategy-noshow',
    label: 'Strategy No-Show',
    desc: 'Strategy meeting missed. Email 2 references specific findings from the return.',
    required: ['icpTag', 'trackTag', 'finding1'],
  },
  {
    id: 'no-engagement',
    label: 'No Engagement Signed',
    desc: 'Strategy happened, no signed engagement. Variants by ICP + Track.',
    required: ['icpTag', 'trackTag'],
  },
];
const STOP_REASONS = [
  ['manual', 'Manual stop (just stop)'],
  ['replied', 'Replied to an email'],
  ['booked', 'Booked the next meeting'],
];

export default function EmailFollowUpView() {
  const { id: prospectId } = useParams();
  const { prospects } = usePipeline();
  const prospect = useMemo(() => (prospects || []).find(p => p.id === prospectId), [prospects, prospectId]);

  const [state, setState] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [automations, setAutomations] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [actionInFlight, setActionInFlight] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);
  const saveTimer = useRef(null);
  const dirty = useRef(false);

  // Initial load
  const load = useCallback(async () => {
    if (!prospectId) return;
    setLoadError(null);
    try {
      const r = await apiFetch(`/api/data/email-followup?prospectId=${encodeURIComponent(prospectId)}`);
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setState(j.state || {});
      setEngagement(j.engagement || null);
      setAutomations(j.automations || null);
      setLoaded(true);
    } catch (e) {
      setLoadError(e.message);
      setLoaded(true);
    }
  }, [prospectId]);
  useEffect(() => { load(); }, [load]);

  // Auto-save personalization changes (debounced 800ms)
  function updateField(key, value) {
    setState(prev => ({ ...(prev || {}), [key]: value }));
    dirty.current = true;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      if (!dirty.current) return;
      setSaving(true);
      setSaveError(null);
      try {
        const snapshot = { ...(state || {}), [key]: value };
        const r = await apiFetch('/api/data/email-followup', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            prospectId,
            icpTag: snapshot.icpTag ?? null,
            trackTag: snapshot.trackTag ?? null,
            discoveryPainPoint: snapshot.discoveryPainPoint ?? null,
            finding1: snapshot.finding1 ?? null,
            finding2: snapshot.finding2 ?? null,
            finding3: snapshot.finding3 ?? null,
          }),
        });
        const j = await r.json();
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
        if (j.state) setState(j.state);
        setSavedAt(new Date());
        dirty.current = false;
      } catch (e) {
        setSaveError(e.message);
      } finally {
        setSaving(false);
      }
    }, 800);
  }

  async function startSequence(sequence) {
    setActionInFlight(true);
    setActionMsg(null);
    try {
      const r = await apiFetch('/api/data/email-followup/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prospectId, sequence }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setState(j.state);
      setActionMsg({ type: 'ok', text: `Started ${sequence}. AC will fire the first email shortly.` });
      load(); // refresh engagement
    } catch (e) {
      setActionMsg({ type: 'err', text: e.message });
    } finally {
      setActionInFlight(false);
    }
  }

  async function stopSequence(reason) {
    if (!window.confirm(`Stop the active sequence (reason: ${reason})? AC will remove the contact from the automation.`)) return;
    setActionInFlight(true);
    setActionMsg(null);
    try {
      const r = await apiFetch('/api/data/email-followup/stop', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prospectId, reason }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setState(j.state);
      setActionMsg({ type: 'ok', text: 'Sequence stopped.' });
      load();
    } catch (e) {
      setActionMsg({ type: 'err', text: e.message });
    } finally {
      setActionInFlight(false);
    }
  }

  // Validation: which sequences can be started?
  function canStart(seq) {
    if (state?.currentSequence) return false; // can't start while one is active
    return (seq.required || []).every(field => state?.[field]);
  }
  function missingFor(seq) {
    return (seq.required || []).filter(field => !state?.[field]);
  }

  return (
    <MeetingFormLayout
      title="Email Follow-Up"
      prospect={prospect}
      savedAt={savedAt}
      saving={saving}
      loadError={loadError}
      saveError={saveError}
    >
      {!loaded ? (
        <div className="text-[12px] text-[#888] p-4">Loading email follow-up state…</div>
      ) : (
        <>
          {/* Current state */}
          <Card>
            <div className="flex items-center justify-between mb-2">
              <SectionLabel>Current State</SectionLabel>
              {state?.acContactId && (
                <span className="text-[10px] text-[#aaa]">AC contact: {state.acContactId}</span>
              )}
            </div>
            {state?.currentSequence ? (
              <div className="grid grid-cols-2 gap-3 text-[12px]">
                <Stat label="Active sequence" value={sequenceLabel(state.currentSequence)} accent="text-deep-teal" />
                <Stat label="Started" value={fmtDate(state.startedAt)} />
              </div>
            ) : state?.stoppedAt ? (
              <div className="grid grid-cols-2 gap-3 text-[12px]">
                <Stat label="No active sequence" value="—" subtitle={`Last stopped ${fmtDate(state.stoppedAt)} (${state.stopReason})`} />
                <div />
              </div>
            ) : (
              <div className="text-[12px] text-[#888]">No sequence has been started yet.</div>
            )}
          </Card>

          {/* Personalization fields */}
          <Card>
            <SectionLabel>Personalization (auto-saves)</SectionLabel>
            <Note>These map to AC tags + custom fields on the contact. Synced live as you edit.</Note>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <SelectField label="ICP" value={state?.icpTag || ''} onChange={v => updateField('icpTag', v || null)} options={ICP_OPTIONS} />
              <SelectField label="Track" value={state?.trackTag || ''} onChange={v => updateField('trackTag', v || null)} options={TRACK_OPTIONS} />
            </div>
            <TextField
              label="Discovery pain point (Sequence 2 Email 1)"
              value={state?.discoveryPainPoint || ''}
              onChange={v => updateField('discoveryPainPoint', v)}
              placeholder={`e.g., "you've been getting hit with surprise tax bills every April"`}
              minHeight={60}
            />
            <Note>
              Write as a sentence continuation (lowercase, no period). Email 1 reads:
              <span className="italic"> "You mentioned that <strong>{state?.discoveryPainPoint || '<your text here>'}</strong>."</span>
            </Note>
            <div className="grid grid-cols-1 gap-2 mt-3">
              <TextField label="Finding 1 (Sequence 3 Email 2)" value={state?.finding1 || ''} onChange={v => updateField('finding1', v)} placeholder='e.g., "$96K of suspended passive losses sitting unused"' minHeight={50} />
              <TextField label="Finding 2 (optional)" value={state?.finding2 || ''} onChange={v => updateField('finding2', v)} minHeight={50} />
              <TextField label="Finding 3 (optional)" value={state?.finding3 || ''} onChange={v => updateField('finding3', v)} minHeight={50} />
            </div>
            <Note>
              Findings render as bullet points in Sequence 3 Email 2. Write each as a complete phrase
              (e.g., "$96K of suspended passive losses sitting unused"). Capitalize the first letter; no period.
            </Note>
          </Card>

          {/* Actions */}
          <Card>
            <SectionLabel>Sequence Actions</SectionLabel>
            {actionMsg && (
              <div className={`mt-2 text-[11px] p-2 rounded-[3px] ${actionMsg.type === 'err' ? 'bg-rose/10 text-rose' : 'bg-deep-teal/10 text-deep-teal'}`}>
                {actionMsg.text}
              </div>
            )}
            {state?.currentSequence ? (
              <div className="mt-2">
                <Note>An active sequence is in progress. Stop it before starting another.</Note>
                <div className="flex flex-wrap gap-2 mt-2">
                  {STOP_REASONS.map(([reason, label]) => (
                    <button
                      key={reason}
                      onClick={() => stopSequence(reason)}
                      disabled={actionInFlight}
                      className={`text-[11px] font-bold px-3 py-1.5 rounded-[3px] ${
                        actionInFlight ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-rose text-white hover:bg-rose/90'
                      }`}
                    >
                      Stop: {label}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-2 mt-2">
                <Note>Pick the sequence to start. Required personalization fields are validated inline.</Note>
                {SEQUENCES.map(seq => {
                  const ok = canStart(seq);
                  const missing = missingFor(seq);
                  return (
                    <div key={seq.id} className="border border-gray-200 rounded-[3px] p-3">
                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <div className="text-[13px] font-bold text-navy">{seq.label}</div>
                          <div className="text-[11px] text-[#666] mt-0.5">{seq.desc}</div>
                          {!ok && missing.length > 0 && (
                            <div className="text-[10px] text-amber-600 mt-1">
                              Missing: {missing.map(prettyField).join(', ')}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => startSequence(seq.id)}
                          disabled={!ok || actionInFlight}
                          className={`text-[11px] font-bold px-3 py-1.5 rounded-[3px] shrink-0 ${
                            ok && !actionInFlight
                              ? 'bg-bullogic-blue text-white hover:bg-bullogic-blue/90'
                              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          Start
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Engagement timeline */}
          <Card>
            <SectionLabel>Email Engagement</SectionLabel>
            <Note>Live from ActiveCampaign. Refreshes when this page loads. Per-email rows appear AFTER AC sends the email (not when queued). For new enrollments, expect a delay equal to the first wait step in the sequence.</Note>

            {/* Active automations — surfaces enrollment status even before
                any emails have actually sent. AC's contactCampaigns endpoint
                only records sends, so without this section, a freshly
                enrolled contact looks like "nothing's happening" until the
                first email fires (1 hour to 1 day later per sequence). */}
            {automations?.automations && automations.automations.length > 0 && (
              <div className="mt-3 space-y-3">
                {automations.automations.map(a => {
                  const statusLabel =
                    a.status === '1' || a.status === 1 ? 'active'
                    : a.status === '2' || a.status === 2 ? 'completed'
                    : a.status === '3' || a.status === 3 ? 'exited (goal reached)'
                    : `status ${a.status}`;
                  const pctComplete = a.totalElements
                    ? Math.round((a.completedElements / a.totalElements) * 100)
                    : 0;
                  return (
                    <div key={a.contactAutomationId} className="p-3 bg-bullogic-blue/5 rounded-[3px] border border-bullogic-blue/20">
                      <div className="flex items-baseline justify-between mb-2">
                        <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-bullogic-blue">
                          Automation {a.automationId} · {statusLabel}
                        </div>
                        <div className="text-[10px] text-[#888]">
                          {a.completedElements} of {a.totalElements} steps {a.totalElements > 0 && `(${pctComplete}%)`}
                        </div>
                      </div>
                      <div className="text-[11px] text-[#666] mb-2">
                        {a.addedAt && <>Enrolled {fmtDate(a.addedAt)}</>}
                        {a.lastActivityAt && <> · last activity {fmtDate(a.lastActivityAt)}</>}
                      </div>
                      {a.logs && a.logs.length > 0 && (
                        <div className="border-t border-bullogic-blue/15 pt-2 mt-1">
                          <div className="text-[9px] font-bold uppercase tracking-[0.08em] text-[#888] mb-1">Step Timeline ({a.logs.length})</div>
                          <ol className="space-y-1 list-none pl-0">
                            {a.logs.map((log, i) => {
                              const isSend = log.type === 'send';
                              const isBranch = log.type === 'if' || log.type === 'else';
                              return (
                                <li key={i} className="text-[11px] flex gap-2 items-baseline">
                                  <span className="text-[#aaa] font-mono w-4 text-right">{i + 1}.</span>
                                  <span className="text-[#666] w-32 flex-shrink-0">{fmtDateTime(log.timestamp)}</span>
                                  <span className="text-[#aaa]">·</span>
                                  <span className={`flex-1 ${
                                    isSend ? 'text-deep-teal font-bold'
                                    : isBranch ? 'text-[#888]'
                                    : 'text-navy'
                                  }`}>
                                    {isSend && '✉ '}
                                    {log.label || 'Step'}
                                  </span>
                                </li>
                              );
                            })}
                          </ol>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {(() => {
              // If the timeline above shows any send-type log, we already
              // have proof emails fired. Skip the empty-engagement fallback
              // to avoid contradicting the timeline.
              const hasSendInTimeline = (automations?.automations || []).some(
                a => (a.logs || []).some(l => l.type === 'send')
              );
              if (hasSendInTimeline) return null;
              if (!engagement) {
                return <div className="text-[12px] text-[#888] mt-2">No AC contact yet. Engagement appears once a sequence is started.</div>;
              }
              if (engagement.error) {
                return <div className="text-[11px] text-rose mt-2">Couldn't fetch engagement: {engagement.error}</div>;
              }
              if (!engagement.campaigns || engagement.campaigns.length === 0) {
                return (
                  <div className="text-[12px] text-[#888] mt-2">
                    {automations?.automations && automations.automations.length > 0
                      ? 'Enrolled but no emails have sent yet. The first email fires after the initial wait step (see your sequence cadence).'
                      : 'No emails sent to this contact yet.'}
                  </div>
                );
              }
              return null;
            })()}
            {engagement?.campaigns && engagement.campaigns.length > 0 && (
              <div className="overflow-x-auto mt-2">
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="border-b border-gray-200 text-[10px] font-bold uppercase tracking-[0.08em] text-[#888]">
                      <th className="text-left py-1 pr-3">Sent</th>
                      <th className="text-left py-1 pr-3">Campaign</th>
                      <th className="text-right py-1 px-2">Opens</th>
                      <th className="text-right py-1 px-2">Clicks</th>
                      <th className="text-right py-1 px-2">Replies</th>
                      <th className="text-right py-1 pl-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {engagement.campaigns.map(c => (
                      <tr key={c.campaignId} className="border-b border-gray-50">
                        <td className="py-1 pr-3 text-[#666]">{fmtDate(c.sentAt)}</td>
                        <td className="py-1 pr-3 text-navy font-mono text-[11px]">{c.campaignId}</td>
                        <td className="py-1 px-2 text-right tabular-nums">{c.opens}</td>
                        <td className="py-1 px-2 text-right tabular-nums">{c.clicks}</td>
                        <td className="py-1 px-2 text-right tabular-nums">{c.replies}</td>
                        <td className="py-1 pl-2 text-right text-[10px] text-[#666]">
                          {c.unsubscribed ? 'unsub' : c.bounce ? 'bounce' : 'sent'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </MeetingFormLayout>
  );
}

// ─── Bits ────────────────────────────────────────────────────────
const inputCls = 'w-full px-3 py-2 text-[13px] border border-gray-300 rounded-[3px] focus:outline-none focus:border-bullogic-blue';

function Card({ children }) {
  return <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">{children}</div>;
}
function SectionLabel({ children }) {
  return <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#888]">{children}</div>;
}
function Note({ children }) { return <p className="text-[11px] text-[#666] mt-1">{children}</p>; }
function Label({ children }) { return <label className="block text-[10px] font-bold uppercase tracking-wider text-navy mt-2 mb-1">{children}</label>; }
function TextField({ label, value, onChange, placeholder, minHeight = 56 }) {
  return (
    <div>
      <Label>{label}</Label>
      <textarea
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className={inputCls}
        style={{ minHeight, resize: 'vertical' }}
      />
    </div>
  );
}
function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <Label>{label}</Label>
      <select value={value || ''} onChange={e => onChange(e.target.value)} className={inputCls}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
function Stat({ label, value, subtitle, accent = 'text-navy' }) {
  return (
    <div>
      <div className="text-[9px] font-bold uppercase tracking-[0.1em] text-[#888]">{label}</div>
      <div className={`text-[18px] font-bold leading-tight ${accent}`}>{value}</div>
      {subtitle && <div className="text-[10px] text-[#888] mt-0.5">{subtitle}</div>}
    </div>
  );
}
function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return s; }
}
function fmtDateTime(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return s; }
}
function sequenceLabel(id) {
  return SEQUENCES.find(s => s.id === id)?.label || id;
}
function prettyField(f) {
  return ({
    icpTag: 'ICP',
    trackTag: 'Track',
    discoveryPainPoint: 'Discovery pain point',
    finding1: 'Finding 1',
  }[f]) || f;
}
